// web/src/features/home/useHomeLayout.ts
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { HomeLayout, LayoutScope } from './types';

export type LayoutResp = { scope: LayoutScope; layout: HomeLayout };

// The user's effective Home layout. Shared with the greeting bar and the
// Weather tile (useTempUnit), which read its preferences from the same cache.
export const homeLayoutQuery = {
  queryKey: ['home-layout'],
  queryFn: () => api.get<LayoutResp>('/home/layout'),
};

export function useHomeLayout() {
  const qc = useQueryClient();
  const q = useQuery(homeLayoutQuery);

  const save = useCallback(async (layout: HomeLayout) => {
    await api.put('/home/layout', layout);
    await qc.invalidateQueries({ queryKey: ['home-layout'] });
  }, [qc]);

  const reset = useCallback(async () => {
    await api.del('/home/layout');
    await qc.invalidateQueries({ queryKey: ['home-layout'] });
  }, [qc]);

  const saveRole = useCallback(async (roleKey: string, layout: HomeLayout) => {
    await api.put(`/home/layout?scope=role&roleKey=${encodeURIComponent(roleKey)}`, layout);
  }, []);

  const loadRole = useCallback((roleKey: string) =>
    api.get<LayoutResp>(`/home/layout?scope=role&roleKey=${encodeURIComponent(roleKey)}`), []);

  return {
    layout: q.data?.layout ?? { version: 1, widgets: [] },
    scope: q.data?.scope ?? 'system',
    isLoading: q.isLoading,
    save, reset, saveRole, loadRole,
  };
}
